import type { FC, ReactNode } from "react";

import classNames from "classnames";

import { Button, type ButtonVariant } from "@jti/core-components/components/Button";

import styles from "./DetailsBar.module.scss";

type DetailsBarButtonVariant = Extract<ButtonVariant, "primary" | "secondary">;

type ActionComponentProps = {
  buttonType?: "button" | "text";
  text: string;
  url?: string;
  buttonVariant: DetailsBarButtonVariant;
  onClick?: () => void;
};

export type DetailsBarProps = {
  withDate?: boolean;
  buttonVariant?: DetailsBarButtonVariant;
  variant?: "default" | "compact";
  leftItem: ReactNode;
  leftDescription: string;
  rightTitle?: ReactNode;
  rightDescription?: ReactNode;
  buttonLabel?: string;
  buttonUrl?: string;
  buttonType?: "button" | "text";
  onButtonClick?: () => void;
  barTitle?: string;
  hideLeftBlock?: boolean;
};

const ActionComponent: FC<ActionComponentProps> = ({ buttonType, text, url, buttonVariant, onClick }) => {
  if (buttonType === "text") {
    return <div className={classNames(styles.actionText)}>{text}</div>;
  }

  if (buttonType === "button" && text && (url || onClick)) {
    const buttonProps = url ? { href: url } : { onClick, type: "button" as const };

    return (
      <>
        <div className={styles.desktopButton}>
          <Button {...buttonProps} variant={buttonVariant} className={classNames(styles.actionButton)}>
            {text}
          </Button>
        </div>
        <div className={styles.mobileButton}>
          <Button {...buttonProps} size="sm" fullWidth variant={buttonVariant} className={styles.actionButton}>
            {text}
          </Button>
        </div>
      </>
    );
  }

  return null;
};

export const DetailsBar = ({
  leftItem,
  rightTitle,
  rightDescription,
  buttonLabel,
  buttonUrl,
  buttonType,
  onButtonClick,
  leftDescription,
  buttonVariant = "secondary",
  variant = "default",
  withDate,
  barTitle,
  hideLeftBlock,
}: DetailsBarProps) => {
  const isCompact = variant === "compact";

  const classes = classNames(styles.detailsBar, {
    [styles.withDate]: withDate,
    [styles.hideLeftBlock]: hideLeftBlock,
    [styles.compact]: isCompact,
  });

  return (
    <div>
      {barTitle && (
        <div data-testid="displayBarTitle" className={styles.barTitle}>
          {barTitle}
        </div>
      )}
      <div className={classes}>
        <div className={styles.header}>
          <div className={styles.leftBlock}>
            <div className={styles.leftBlockItem}>{leftItem}</div>
            <div className={styles.leftBlockDescription}>{leftDescription}</div>
          </div>
        </div>
        <div className={styles.rightBlock}>
          {rightTitle && (
            <div className={styles.rightBlockTitle} data-testid="detailsBarRightBlockTitle">
              {rightTitle}
            </div>
          )}
          <div className={styles.rightBlockDescription}>{rightDescription}</div>
        </div>
        <ActionComponent
          buttonVariant={buttonVariant}
          buttonType={buttonType}
          text={buttonLabel ?? ""}
          url={buttonUrl ?? ""}
          onClick={onButtonClick}
        />
      </div>
    </div>
  );
};
